import { clsx } from 'clsx'
import { DragEvent, ReactNode } from 'react'
import { Tip } from './Tip'

/**
 * We’re replacing the HTML table/thead/tr/td with <div> + CSS Grid.
 *
 * For consistent columns:
 *  - The first two columns are ~2rem wide (matching w-8, which is 2rem).
 *  - The third column is ~13rem (matching w-52).
 *  - The fourth column ~16rem (matching w-64).
 *  - The fifth column ~12rem (matching w-48).
 *  - The sixth column ~5rem (matching w-20).
 *
 * This is done via `grid-cols-[2rem_2rem_13rem_16rem_12rem_5rem]`.
 * Each row’s <div> sets `display: grid` with these fixed columns.
 */
export const Checklist = {
  /**
   * The main "table" container. We define one grid row for the "header"
   * and wrap the children (the body rows) in another <div>.
   */
  Table({ children }: { children: ReactNode }) {
    return (
      <div className="w-full overflow-hidden font-sans">
        {/* Header row */}
        <div className="grid grid-cols-[2rem_2rem_13rem_16rem_12rem_5rem] border-b border-b-gray-300">
          {/* Each "header cell" is just a <div> now */}
          <div className="w-8 py-3.5 pl-4 text-left text-xs font-thin uppercase text-gray-500" />
          <div className="w-8 py-3.5 pl-4 text-left text-xs font-thin uppercase text-gray-500" />
          <div className="w-52 py-3.5 pr-3 text-left text-xs font-thin uppercase text-gray-500">
            Name
          </div>
          <div className="w-64 py-3.5 text-left text-xs font-thin uppercase text-gray-500">
            Note
          </div>
          <div className="w-48 py-3.5 text-left text-xs font-thin uppercase text-gray-500">
            Link
          </div>
          <div className="w-20 py-3.5 pl-4 pr-3 text-left text-xs font-thin uppercase text-gray-500" />
        </div>

        {/* Body rows go here */}
        <div className="divide-y divide-gray-200">{children}</div>
      </div>
    )
  },

  /**
   * Each "Row" is now a <div> with the exact same grid definition as the header,
   * so columns will align. We just replace <tr> with <div> and each cell is <div>.
   */
  Row({
    children,
    checked,
    draggable,
    onDragStart,
    onDragOver,
    onDrop,
    onDragLeave,
    dragStatus
  }: {
    children: ReactNode
    checked?: boolean
    draggable?: boolean
    onDragStart?: (event: DragEvent<HTMLElement>) => void
    onDragOver?: (event: DragEvent<HTMLElement>) => void
    onDrop?: (event: DragEvent<HTMLElement>) => void
    onDragLeave?: (event: DragEvent<HTMLElement>) => void
    dragStatus?: 'before' | 'after' | 'dragging'
  }) {
    return (
      <div
        className={clsx('group grid h-11 grid-cols-[2rem_2rem_13rem_16rem_12rem_5rem]', {
          'hover:bg-stone-100': !checked,
          'bg-blue-100 hover:bg-blue-200': checked,
          'shadow-[inset_0_2px_0_0_#354dcc]': dragStatus === 'before',
          'shadow-[inset_0_-2px_0_0_#354dcc]': dragStatus === 'after',

          'opacity-80': dragStatus === 'dragging'
        })}
        draggable={draggable}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragLeave={onDragLeave}
      >
        {children}
      </div>
    )
  },

  /**
   * The "drag handle" cell. Now just a <div> that takes up a column in the grid.
   */
  DragHandleCell({ enabled = true }: { enabled?: boolean }) {
    return (
      <div className="relative flex items-center whitespace-nowrap px-2 py-2 text-sm text-gray-500">
        {enabled && <DragHandle className="invisible cursor-move group-hover:visible" />}
      </div>
    )
  },

  /**
   * Checkbox cell. Replaces <td> with a <div>.
   */
  CheckboxCell(props: { checked: boolean; onChange: (newValue: boolean) => void }) {
    return (
      <div className="relative flex items-center justify-center whitespace-nowrap px-2 py-2 text-sm text-gray-500">
        <input
          type="checkbox"
          className="border-gray-300 px-2"
          checked={props.checked}
          onChange={(e) => props.onChange(e.target.checked)}
        />
      </div>
    )
  },

  /**
   * MainCell, used for the primary text content. Just a <div> in the row grid.
   */
  MainCell({ children, crossedOut }: { children: ReactNode; crossedOut?: boolean }) {
    return (
      <div
        className={clsx(
          'flex flex-row items-center gap-2 whitespace-nowrap px-2 py-2 text-base font-semibold text-gray-900',
          {
            'line-through': crossedOut
          }
        )}
      >
        {children}
      </div>
    )
  },

  /**
   * Secondary text content cell ("Note" column).
   */
  NoteCell({ children }: { children: ReactNode }) {
    return (
      <div className="flex items-center truncate whitespace-nowrap px-2 py-2 text-base text-gray-700">
        {children}
      </div>
    )
  },

  /**
   * Link cell. Another <div>.
   */
  LinkCell({ children }: { children?: ReactNode }) {
    return (
      <div className="flex flex-row items-center gap-2 whitespace-nowrap px-2 py-2 text-sm text-gray-500">
        {children}
      </div>
    )
  },

  /**
   * ActionGroup cell, containing the "invisible until hover" action buttons.
   */
  ActionGroup({ children }: { children: ReactNode }) {
    return (
      <div className="invisible flex flex-row items-center gap-2 whitespace-nowrap px-3 py-2 text-sm text-gray-500 group-hover:visible">
        {children}
      </div>
    )
  },

  /**
   * Button with a tooltip, same as before—just not a table cell now.
   */
  Button({ children, tip, onClick }: { children?: ReactNode; tip?: string; onClick: () => void }) {
    return (
      <Tip title={tip}>
        <button
          className="relative h-6 max-h-[40px] w-6 max-w-[40px] select-none rounded-lg text-center align-middle font-sans text-xs font-medium uppercase text-gray-900 transition-all hover:bg-gray-900/10 active:bg-gray-900/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button"
          onClick={onClick}
        >
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
            {children}
          </span>
        </button>
      </Tip>
    )
  },

  /**
   * Footer row—if needed. You can style it similarly, or keep it as a block.
   */
  Footer(props: { children: ReactNode }) {
    return <div>{props.children}</div>
  }
}

/**
 * The SVG drag-handle. Unchanged from your original code.
 */
function DragHandle({ className }: { className?: string }) {
  return (
    <svg width="8" height="12" viewBox="0 0 8 12" fill="none" className={className}>
      <path
        d="M2.75 1.75C2.75 1.94778 2.69135 2.14112 2.58147 2.30557C2.47159 2.47002 2.31541 2.59819 2.13268 2.67388C1.94996 2.74957 1.74889 2.76937 1.55491 2.73079C1.36093 2.6922 1.18275 2.59696 1.04289 2.45711C0.903041 2.31725 0.8078 2.13907 0.769215 1.94509C0.73063 1.75111 0.750433 1.55004 0.826121 1.36732C0.901809 1.18459 1.02998 1.02841 1.19443 0.918531C1.35888 0.80865 1.55222 0.750001 1.75 0.750001C2.01522 0.750001 2.26957 0.855357 2.45711 1.04289C2.64464 1.23043 2.75 1.48478 2.75 1.75ZM6.25 2.75C6.44778 2.75 6.64112 2.69135 6.80557 2.58147C6.97002 2.47159 7.09819 2.31541 7.17388 2.13268C7.24957 1.94996 7.26937 1.74889 7.23079 1.55491C7.1922 1.36093 7.09696 1.18275 6.95711 1.04289C6.81726 0.903041 6.63907 0.8078 6.44509 0.769215C6.25111 0.73063 6.05004 0.750433 5.86732 0.826121C5.68459 0.901809 5.52841 1.02998 5.41853 1.19443C5.30865 1.35888 5.25 1.55222 5.25 1.75C5.25 2.01522 5.35536 2.26957 5.54289 2.45711C5.73043 2.64464 5.98478 2.75 6.25 2.75ZM1.75 5C1.55222 5 1.35888 5.05865 1.19443 5.16853C1.02998 5.27841 0.901809 5.43459 0.826121 5.61732C0.750433 5.80004 0.73063 6.00111 0.769215 6.19509C0.8078 6.38907 0.903041 6.56725 1.04289 6.70711C1.18275 6.84696 1.36093 6.9422 1.55491 6.98079C1.74889 7.01937 1.94996 6.99957 2.13268 6.92388C2.31541 6.84819 2.47159 6.72002 2.58147 6.55557C2.69135 6.39112 2.75 6.19778 2.75 6C2.75 5.73478 2.64464 5.48043 2.45711 5.29289C2.26957 5.10536 2.01522 5 1.75 5ZM6.25 5C6.05222 5 5.85888 5.05865 5.69443 5.16853C5.52998 5.27841 5.40181 5.43459 5.32612 5.61732C5.25043 5.80004 5.23063 6.00111 5.26922 6.19509C5.3078 6.38907 5.40304 6.56725 5.54289 6.70711C5.68275 6.84696 5.86093 6.9422 6.05491 6.98079C6.24889 7.01937 6.44996 6.99957 6.63268 6.92388C6.81541 6.84819 6.97159 6.72002 7.08147 6.55557C7.19135 6.39112 7.25 6.19778 7.25 6C7.25 5.73478 7.14464 5.48043 6.95711 5.29289C6.76957 5.10536 6.51522 5 6.25 5ZM1.75 9.25C1.55222 9.25 1.35888 9.30865 1.19443 9.41853C1.02998 9.52841 0.901809 9.68459 0.826121 9.86732C0.750433 10.05 0.73063 10.2511 0.769215 10.4451C0.8078 10.6391 0.903041 10.8173 1.04289 10.9571C1.18275 11.097 1.36093 11.1922 1.55491 11.2308C1.74889 11.2694 1.94996 11.2496 2.13268 11.1739C2.31541 11.0982 2.47159 10.97 2.58147 10.8056C2.69135 10.6411 2.75 10.4478 2.75 10.25C2.75 9.98478 2.64464 9.73043 2.45711 9.54289C2.26957 9.35536 2.01522 9.25 1.75 9.25ZM6.25 9.25C6.05222 9.25 5.85888 9.30865 5.69443 9.41853C5.52998 9.52841 5.40181 9.68459 5.32612 9.86732C5.25043 10.05 5.23063 10.2511 5.26922 10.4451C5.3078 10.6391 5.40304 10.8173 5.54289 10.9571C5.68275 11.097 5.86093 11.1922 6.05491 11.2308C6.24889 11.2694 6.44996 11.2496 6.63268 11.1739C6.81541 11.0982 6.97159 10.97 7.08147 10.8056C7.19135 10.6411 7.25 10.4478 7.25 10.25C7.25 9.98478 7.14464 9.73043 6.95711 9.54289C6.76957 9.35536 6.51522 9.25 6.25 9.25Z"
        fill="#545666"
      />
    </svg>
  )
}
